import dayjs from 'dayjs';
import z from 'zod';
import { LegacyExamAttemptStatus, LegacyExamAttemptType } from '../common/enums';
import { GenericTipTapJsonSchema, SubjectSchema, UnixTimestampSchema } from '../common/schemas';
import { getObjectSizeInBytes } from '../common/utils';
import {
  DBLegacyExamExamIdSchema,
  DBLegacyExamLabelSchema,
  LegacyExamCodeSchema,
  LegacyExamIdSchema,
  LegacyExamSubjectSchema,
} from '../legacyExams/schemas';
import { DBLegacyExamTypeTypeSchema } from '../legacyExamTypes/schemas';
import {
  DBStaffFirstNameSchema,
  DBStaffLastNameSchema,
  DBStaffStaffIdSchema,
} from '../staff/schemas';
import {
  DBStudentFirstNameSchema,
  DBStudentLastNameSchema,
  DBStudentStudentIdSchema,
} from '../students/schemas';
import { LegacyExamQuestionAnalyticsType } from './enums';

/**
 * Shared & Helper Schemas
 */

/**
 * DB Schemas
 */

export const DBLegacyExamAttemptStudentSchema = z.lazy(() => DBStudentStudentIdSchema);
export const DBLegacyExamAttemptSubjectExamQuestionIdentifierSchema = z.string().refine(val => {
  const parts = val.split('#');
  return (
    parts.length === 4 &&
    z.literal(LegacyExamAttemptType.LegacyExam).safeParse(parts[0]).success &&
    SubjectSchema.safeParse(parts[1]).success &&
    z.lazy(() => DBLegacyExamExamIdSchema).safeParse(parts[2]).success &&
    z
      .string()
      .regex(/^[0-9]+$/)
      .safeParse(parts[3]).success
  );
});
export const DBLegacyExamAttemptAnalyticsSchema = z.partialRecord(
  z.enum(LegacyExamQuestionAnalyticsType),
  z.object({
    attemptedMarks: z.number().int().min(0),
    inaccuracyMarks: z.number().int().min(0),
    missedMarks: z.number().int().min(0),
    sillyMarks: z.number().int().min(0),
  })
);
export const DBLegacyExamAttemptCreationDatetimeSchema = UnixTimestampSchema;
export const DBLegacyExamAttemptDueDatetimeSchema = UnixTimestampSchema;
export const DBLegacyExamAttemptExamSchema = z.string().refine(val => {
  const parts = val.split('#');

  return (
    parts.length === 2 &&
    SubjectSchema.safeParse(parts[0]).success &&
    z.lazy(() => DBLegacyExamExamIdSchema).safeParse(parts[1]).success
  );
});
export const DBLegacyExamAttemptExamLabelSchema = z.lazy(() => DBLegacyExamLabelSchema);
export const DBLegacyExamAttemptMarkSchema = z.number().int().min(0);
export const DBLegacyExamAttemptMarkedDatetimeSchema = UnixTimestampSchema;
export const DBLegacyExamAttemptMarkerIdSchema = z.lazy(() => DBStaffStaffIdSchema);
export const DBLegacyExamAttemptMarkerCommentSchema = GenericTipTapJsonSchema.refine(
  v => getObjectSizeInBytes(v) <= 20 * 1024
);
export const DBLegacyExamAttemptMaxMarkSchema = z.number().int().min(0);
export const DBLegacyExamAttemptMcqAutomarkSchema = z
  .record(z.string(), z.record(z.string().length(1), z.boolean()))
  .openapi({
    description: 'Record of the automatically marked MCQ options',
    example: {
      '1': { A: true, B: false, C: false, D: false },
      '2': { A: false, B: true, C: false, D: false },
      '3': { A: false, B: false, C: true, D: false },
      '4': { A: false, B: false, C: false, D: true },
      '5': { A: true, B: true, C: false, D: false },
    },
  });
export const DBLegacyExamAttemptOutcomeSchema = z.number().min(0).max(1);
export const DBLegacyExamAttemptQuestionSchema = z.literal('_');
export const DBLegacyExamAttemptStatusSchema = z.string().refine(val => {
  const parts = val.split('#');
  return (
    parts.length === 2 &&
    z.literal(LegacyExamAttemptType.LegacyExam).safeParse(parts[0]).success &&
    z.enum(LegacyExamAttemptStatus).safeParse(parts[1]).success
  );
});
export const DBLegacyExamAttemptStudentDetailsSchema = z.object({
  firstName: z.lazy(() => DBStudentFirstNameSchema),
  lastName: z.lazy(() => DBStudentLastNameSchema),
});
export const DBLegacyExamAttemptMarkerDetailsSchema = z.object({
  firstName: z.lazy(() => DBStaffFirstNameSchema),
  lastName: z.lazy(() => DBStaffLastNameSchema),
});
export const DBLegacyExamAttemptTypeSchema = z.lazy(() => DBLegacyExamTypeTypeSchema);
export const DBLegacyExamAttemptUnmarkedExamFileNameSchema = z.string().max(2048);
export const DBLegacyExamAttemptMarkedExamFileNameSchema = z.string().max(2048);

export const DBLegacyExamAttemptSchema = z.object({
  student: DBLegacyExamAttemptStudentSchema,
  subjectExamQuestionIdentifier: DBLegacyExamAttemptSubjectExamQuestionIdentifierSchema,
  analytics: DBLegacyExamAttemptAnalyticsSchema,
  creationDatetime: DBLegacyExamAttemptCreationDatetimeSchema,
  dueDatetime: DBLegacyExamAttemptDueDatetimeSchema.optional(),
  exam: DBLegacyExamAttemptExamSchema,
  examLabel: DBLegacyExamAttemptExamLabelSchema,
  mark: DBLegacyExamAttemptMarkSchema,
  markedDatetime: DBLegacyExamAttemptMarkedDatetimeSchema.optional(),
  markerDetails: DBLegacyExamAttemptMarkerDetailsSchema,
  markerId: DBLegacyExamAttemptMarkerIdSchema,
  // Optional: legacy/production exam attempts only carry markerComment once a
  // marker leaves feedback (postExamFeedback), and mcqAutomark only when the
  // marking request carried MCQ responses. Marked attempts with neither are
  // valid — mirrors markerComment on DBLegacyExamQuestionAttemptSchema.
  markerComment: DBLegacyExamAttemptMarkerCommentSchema.optional(),
  mcqAutomark: DBLegacyExamAttemptMcqAutomarkSchema.optional(),
  maxMark: DBLegacyExamAttemptMaxMarkSchema,
  outcome: DBLegacyExamAttemptOutcomeSchema,
  question: DBLegacyExamAttemptQuestionSchema,
  studentDetails: DBLegacyExamAttemptStudentDetailsSchema,
  type: DBLegacyExamAttemptTypeSchema,
  unmarkedExamFileName: DBLegacyExamAttemptUnmarkedExamFileNameSchema.optional(),
  status: DBLegacyExamAttemptStatusSchema,
  markedExamFileName: DBLegacyExamAttemptMarkedExamFileNameSchema.optional(),
});

/**
 * Client Schemas
 */

export const LegacyExamAttemptIdSchema = z.string().brand<'LegacyExamAttemptId'>();
export const LegacyExamAttemptStudentIdSchema = DBLegacyExamAttemptStudentSchema;
export const LegacyExamAttemptSubjectExamQuestionIdentifierSchema =
  DBLegacyExamAttemptSubjectExamQuestionIdentifierSchema;
export const LegacyExamAttemptSubjectSchema = z.lazy(() => LegacyExamSubjectSchema);
export const LegacyExamAttemptExamCodeSchema = z.lazy(() => LegacyExamCodeSchema);
export const LegacyExamAttemptAttemptNumberSchema = z.number().int().min(1);
export const LegacyExamAttemptExamIdSchema = z.lazy(() => LegacyExamIdSchema);
export const LegacyExamAttemptAnalyticsSchema = DBLegacyExamAttemptAnalyticsSchema;
export const LegacyExamAttemptCreationDatetimeSchema = DBLegacyExamAttemptCreationDatetimeSchema;
export const LegacyExamAttemptDueDatetimeSchema = DBLegacyExamAttemptDueDatetimeSchema;
export const LegacyExamAttemptExamLabelSchema = DBLegacyExamAttemptExamLabelSchema;
export const LegacyExamAttemptMarkSchema = DBLegacyExamAttemptMarkSchema;
export const LegacyExamAttemptMarkedDatetimeSchema = DBLegacyExamAttemptMarkedDatetimeSchema;
export const LegacyExamAttemptMarkerDetailsSchema = DBLegacyExamAttemptMarkerDetailsSchema;
export const LegacyExamAttemptMarkerIdSchema = DBLegacyExamAttemptMarkerIdSchema;
export const LegacyExamAttemptMarkerCommentSchema = DBLegacyExamAttemptMarkerCommentSchema;
export const LegacyExamAttemptMaxMarkSchema = DBLegacyExamAttemptMaxMarkSchema;
export const LegacyExamAttemptMcqAutomarkSchema = DBLegacyExamAttemptMcqAutomarkSchema;
export const LegacyExamAttemptOutcomeSchema = DBLegacyExamAttemptOutcomeSchema;
export const LegacyExamAttemptStatusSchema = z.enum(LegacyExamAttemptStatus);
export const LegacyExamAttemptStudentDetailsSchema = DBLegacyExamAttemptStudentDetailsSchema;
export const LegacyExamAttemptTypeSchema = DBLegacyExamTypeTypeSchema;
export const LegacyExamAttemptUnmarkedExamFileNameSchema =
  DBLegacyExamAttemptUnmarkedExamFileNameSchema;
export const LegacyExamAttemptMarkedExamFileNameSchema =
  DBLegacyExamAttemptMarkedExamFileNameSchema;

export const LegacyExamAttemptSchema = z.object({
  id: LegacyExamAttemptIdSchema,
  studentId: LegacyExamAttemptStudentIdSchema.meta({ title: 'Student ID' }),
  subject: LegacyExamAttemptSubjectSchema,
  examCode: LegacyExamAttemptExamCodeSchema.meta({ title: 'Exam code' }),
  attemptNumber: LegacyExamAttemptAttemptNumberSchema.meta({ title: 'Attempt number' }),
  examId: LegacyExamAttemptExamIdSchema,
  analytics: LegacyExamAttemptAnalyticsSchema,
  creationDatetime: LegacyExamAttemptCreationDatetimeSchema.default(() => dayjs().valueOf()).meta({
    title: 'Attempt at',
  }),
  dueDatetime: LegacyExamAttemptDueDatetimeSchema.optional().meta({ title: 'Due at' }),
  examLabel: LegacyExamAttemptExamLabelSchema.meta({ title: 'Exam' }),
  mark: LegacyExamAttemptMarkSchema.meta({ title: 'Mark' }),
  markedDatetime: LegacyExamAttemptMarkedDatetimeSchema.optional().meta({ title: 'Marked at' }),
  markerId: LegacyExamAttemptMarkerIdSchema.meta({ title: 'Marker ID' }),
  markerDetails: LegacyExamAttemptMarkerDetailsSchema,
  // Optional — see DBLegacyExamAttemptSchema: absent on marked attempts that received
  // no written feedback / had no auto-marked MCQs.
  markerComment: LegacyExamAttemptMarkerCommentSchema.optional(),
  studentDetails: LegacyExamAttemptStudentDetailsSchema.meta({ title: 'Student details' }),
  maxMark: LegacyExamAttemptMaxMarkSchema.meta({ title: 'Max mark' }),
  outcome: LegacyExamAttemptOutcomeSchema.meta({ title: 'Outcome' }),
  type: LegacyExamAttemptTypeSchema,
  unmarkedExamFileName: LegacyExamAttemptUnmarkedExamFileNameSchema.optional(),
  status: LegacyExamAttemptStatusSchema,
  mcqAutomark: LegacyExamAttemptMcqAutomarkSchema.optional(),
  markedExamFileName: LegacyExamAttemptMarkedExamFileNameSchema.optional(),
});
